import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WaitingProspectsWidget from "@/components/WaitingProspectsWidget";
import { apiClient } from "@/lib/api";
import {
  ShieldCheck, Lock, Hammer, FileText, ArrowRight, Camera, Scale,
  CheckCircle2, XCircle, Award, BadgeCheck, AlertTriangle, Eye, FileCheck,
  HardHat, User as UserIcon, Sparkles, Banknote, Building2, Briefcase, Landmark,
  Home, Building, Factory, Compass, Wallet, Gift, Users, Mail, UserPlus, Handshake,
  Receipt, Clock, Star,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/2ceac54d-3372-4941-a81c-4e3c4e9eea83/images/40a7b223ce96fb64cdd952937c2bee5d20cdd882b37ab767ac0f6c621c7f2180.png";
const ESCROW_IMG = "https://static.prod-images.emergentagent.com/jobs/2ceac54d-3372-4941-a81c-4e3c4e9eea83/images/77ff9eb7375b3b5973baac256c84610c31d32ce944d645c8d0f01a68b032ffc3.png";
const RENO_IMG = "https://images.unsplash.com/photo-1714455657937-f7fa236a80ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxob21lJTIwcmVub3ZhdGlvbiUyMGludGVyaW9yJTIwZGVzaWdufGVufDB8fHx8MTc3ODU3NTYxOXww&ixlib=rb-4.1.0&q=85";
const WORKER_IMG = "https://images.unsplash.com/photo-1646324554833-f0b6a479fa5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd29ya2VyfGVufDB8fHx8MTc3ODg1NDk0Mnww&ixlib=rb-4.1.0&q=85";

// ── Reusable atoms ───────────────────────────────────────────────────────────
const Overline = ({ children, color = "#FF4D00" }) => (
  <div className="mono text-[11px] uppercase tracking-widest font-semibold" style={{ color }}>
    {children}
  </div>
);

const Marquee = () => (
  <div className="overflow-hidden border-y-2 border-[#0A0A0B] bg-white py-3">
    <div className="flex animate-marquee whitespace-nowrap mono text-xs uppercase tracking-widest text-[#0A0A0B]">
      {Array.from({ length: 2 }).map((_, k) => (
        <div key={k} className="flex items-center gap-12 pr-12">
          <span>● Fonds séquestrés Stripe</span>
          <span>● Libération sur preuves photo</span>
          <span>● SIRET vérifié Sirene/INSEE</span>
          <span>● TVA 20% sur factures</span>
          <span>● Commission 10% / 10%</span>
          <span>● Conformité juridique FR</span>
          <span>● Médiation admin</span>
          <span>● 0€ sans chantier</span>
        </div>
      ))}
    </div>
  </div>
);

// ── HERO ─────────────────────────────────────────────────────────────────────
const LiveCounter = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    apiClient.get("/stats/live").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const { missions_week: w, missions_total: t } = stats;
  let label = null;
  if (w >= 2) label = `${w} missions publiées cette semaine`;
  else if (t >= 5) label = `${t} chantiers sécurisés sur Batisur`;
  if (!label) return null;
  return (
    <div
      className="inline-flex items-center gap-2 self-start border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white px-3 py-1.5 mono text-[11px] uppercase tracking-widest mb-8 shadow-[4px_4px_0_#FF4D00]"
      data-testid="live-counter"
    >
      <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" /> {label}
    </div>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden border-b-2 border-[#0A0A0B]">
    <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
    <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 relative">
      <div className="lg:col-span-7 flex flex-col justify-center">
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 self-start border-2 border-[#0A0A0B] bg-white px-3 py-1.5 mono text-[11px] uppercase tracking-widest mb-8 shadow-[4px_4px_0_#0A0A0B]">
            <span className="w-2 h-2 bg-[#FF4D00]" /> Plateforme française · BTP sécurisé
          </div>
          <LiveCounter />
        </div>
        <h1 className="display text-[clamp(2rem,8vw,5.5rem)] tracking-tighter leading-[0.95]">
          L'argent est <span className="text-[#FF4D00]">bloqué</span>.<br />
          Le travail est <span className="underline decoration-[#FF4D00] decoration-[6px] underline-offset-4">prouvé</span>.<br />
          Vous êtes <span className="bg-[#0A0A0B] text-white px-2">serein</span>.
        </h1>
        <p className="mt-8 text-lg text-zinc-700 max-w-xl">
          La 1ère plateforme française qui <strong>protège</strong> particuliers, architectes, promoteurs et syndics
          contre les abandons de chantier — et <strong>garantit</strong> aux artisans d'être payés à 100%.
          Preuves photo + certificat signé à l'appui.
        </p>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/register?role=demandeur" data-testid="hero-cta-client">
            <button className="btn-batisur flex items-center gap-2 shadow-[6px_6px_0_#0A0A0B] hover:shadow-[3px_3px_0_#0A0A0B] hover:translate-x-[3px] hover:translate-y-[3px] transition">
              <UserIcon className="w-4 h-4" /> Démarrer mon projet · Sécuriser mes travaux <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <Link to="/register?role=artisan&trial=1" data-testid="hero-cta-artisan">
            <button className="btn-anthracite flex items-center gap-2 shadow-[6px_6px_0_#FF4D00] hover:shadow-[3px_3px_0_#FF4D00] hover:translate-x-[3px] hover:translate-y-[3px] transition">
              <HardHat className="w-4 h-4" /> Rejoindre la plateforme · Tester les outils 🎁
            </button>
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-3 max-w-md gap-0 border-2 border-[#0A0A0B] bg-white shadow-[4px_4px_0_#0A0A0B]">
          {[
            ["100%", "Fonds bloqués"],
            ["0€", "Impayés artisans"],
            ["48H", "Libération"],
          ].map(([n, l]) => (
            <div key={n} className="p-4 border-r-2 border-[#0A0A0B] last:border-r-0">
              <div className="display text-3xl">{n}</div>
              <div className="mono text-[10px] uppercase tracking-wider text-zinc-600 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5 relative">
        <div className="relative border-2 border-[#0A0A0B] overflow-hidden bg-white shadow-[8px_8px_0_#0A0A0B]">
          <img src={HERO_IMG} alt="Chantier sécurisé" className="w-full h-[520px] object-cover" />
          <div className="absolute top-4 left-4 bg-white border-2 border-[#0A0A0B] px-3 py-2 flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#FF4D00]" />
            <span className="mono text-[11px] uppercase font-semibold">Escrow Stripe · Actif</span>
          </div>
          <div className="absolute bottom-4 right-4 bg-[#FF4D00] text-white px-3 py-2 shadow-[4px_4px_0_#0A0A0B] border-2 border-[#0A0A0B]">
            <div className="mono text-[10px] uppercase tracking-widest opacity-80">Mission #B-0427</div>
            <div className="display text-lg leading-tight">12 400 €</div>
            <div className="mono text-[10px]">Séquestrés</div>
          </div>
          <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-2 flex items-center gap-2 border-2 border-[#0A0A0B]">
            <BadgeCheck className="w-4 h-4" />
            <span className="mono text-[11px] uppercase font-semibold">SIRET vérifié</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ── Social proof ─────────────────────────────────────────────────────────────
const SocialProof = () => (
  <section className="border-b-2 border-[#0A0A0B] bg-white">
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 divide-y-2 md:divide-y-0 md:divide-x-2 divide-[#0A0A0B]">
      {[
        { v: "100%", l: "Fonds séquestrés via Stripe", i: ShieldCheck },
        { v: "0€", l: "D'impayé pour nos artisans", i: Banknote },
        { v: "INSEE", l: "Identité légale & SIRET vérifiés", i: BadgeCheck },
      ].map(({ v, l, i: Icon }) => (
        <div key={l} className="p-8 lg:p-10 flex items-center gap-5">
          <div className="w-14 h-14 border-2 border-[#0A0A0B] bg-[#F7F7F9] flex items-center justify-center flex-shrink-0">
            <Icon className="w-7 h-7 text-[#FF4D00]" strokeWidth={2} />
          </div>
          <div>
            <div className="display text-4xl lg:text-5xl tracking-tighter leading-none">{v}</div>
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-600 mt-2">{l}</div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ── Live Trust Stats (anonymous aggregate) ───────────────────────────────────
const fmtCompactEUR = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".0", "")} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${Math.round(n)} €`;
};

const TrustStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    apiClient.get("/public/trust-stats").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return null;
  const items = [
    { v: fmtCompactEUR(s.escrow_secured_total_eur || 0), l: "Sécurisés en séquestre" },
    { v: String(s.validated_missions || 0), l: "Chantiers validés" },
    { v: String(s.certificates_emitted || 0), l: "Certificats Batisur émis" },
    { v: String(s.active_artisans || 0), l: "Artisans vérifiés" },
  ];
  return (
    <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B]" data-testid="trust-stats">
      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Preuve sociale · Données en temps réel</div>
            <h2 className="display text-4xl lg:text-5xl tracking-tighter mt-3 max-w-2xl">
              La confiance, <span className="text-[#FF4D00]">chiffrée</span>.
            </h2>
          </div>
          <p className="text-zinc-400 text-sm max-w-md">
            Statistiques anonymisées remontant directement de notre infrastructure de séquestre. Aucune information personnelle exposée.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 border-2 border-white/20">
          {items.map((it, idx) => (
            <div
              key={it.l}
              className={`p-6 lg:p-8 ${idx < items.length - 1 ? "border-r-0 lg:border-r-2 border-white/20" : ""} ${idx < 2 ? "border-b-2 lg:border-b-0 border-white/20" : ""}`}
              data-testid={`trust-stat-${idx}`}
            >
              <div className="display text-5xl lg:text-6xl tracking-tighter text-white leading-none">{it.v}</div>
              <div className="mono text-[10px] lg:text-[11px] uppercase tracking-widest text-zinc-400 mt-4">{it.l}</div>
            </div>
          ))}
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-6">
          Compteurs mis à jour à chaque chargement · Source : `/api/public/trust-stats`
        </div>
      </div>
    </section>
  );
};

// ── Audience targets (10 demandeur types) ──────────────────────────────────
const AudienceTargets = () => {
  const targets = [
    { i: Home, label: "Particuliers", sub: "Rénovation, extension, salle de bain…" },
    { i: Compass, label: "Architectes", sub: "Sécurisez vos clients sur vos projets" },
    { i: Building2, label: "Promoteurs immobiliers", sub: "Volumes & multi-corps de métier" },
    { i: HardHat, label: "Constructeurs CMI", sub: "Maisons individuelles clé en main" },
    { i: Building, label: "Syndics de copro", sub: "Travaux parties communes" },
    { i: Landmark, label: "Bailleurs / Foncières", sub: "Patrimoine locatif sécurisé" },
    { i: Factory, label: "Entreprises BTP", sub: "Donneurs d'ordre & sous-traitance" },
    { i: Briefcase, label: "Collectivités", sub: "Mairies & établissements publics" },
    { i: FileText, label: "Maîtres d'œuvre", sub: "Coordination indépendante" },
    { i: Wallet, label: "Investisseurs", sub: "Marchands de biens, rénovateurs" },
  ];
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-white py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <Overline>Pour 10 profils de demandeurs</Overline>
            <h2 className="display text-4xl lg:text-5xl tracking-tighter mt-3 max-w-3xl">
              Pas qu'un outil pour particuliers.<br />
              <span className="text-[#FF4D00]">Une infrastructure pour toute la maîtrise d'ouvrage française.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-0 border-2 border-[#0A0A0B]">
          {targets.map(({ i: Icon, label, sub }, idx) => (
            <div
              key={label}
              className={`p-6 ${idx % 5 !== 4 ? "border-r-2" : ""} ${idx < targets.length - 5 ? "border-b-2" : ""} border-[#0A0A0B] hover:bg-[#FF4D00] hover:text-white transition group cursor-default`}
              data-testid={`audience-${idx}`}
            >
              <Icon className="w-6 h-6 text-[#FF4D00] group-hover:text-white transition" strokeWidth={2} />
              <div className="display text-lg mt-3 leading-tight">{label}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-white/80 mt-2 leading-tight transition">{sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Particulier focus ────────────────────────────────────────────────────────
const PersonaParticulier = () => (
  <section className="border-b-2 border-[#0A0A0B] bg-[#F7F7F9] py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 relative">
        <div className="border-2 border-[#0A0A0B] bg-white overflow-hidden shadow-[8px_8px_0_#0A0A0B]">
          <img src={RENO_IMG} alt="Rénovation finie" className="w-full h-[440px] object-cover" />
        </div>
        {/* Floating "journal photo" mock */}
        <div className="absolute -bottom-6 -right-2 w-64 bg-white border-2 border-[#0A0A0B] p-4 shadow-[6px_6px_0_#FF4D00]" data-testid="particulier-journal-mock">
          <div className="mono text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1">
            <Camera className="w-3 h-3" /> Journal — étape Finition
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1">
            <div className="aspect-square bg-zinc-200" />
            <div className="aspect-square bg-zinc-300" />
            <div className="aspect-square bg-zinc-200" />
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px]">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span className="font-semibold">3 preuves publiées</span>
          </div>
          <button className="w-full mt-3 bg-emerald-600 text-white mono text-[10px] uppercase tracking-widest font-semibold py-2 hover:bg-emerald-700 transition">
            Valider et libérer
          </button>
        </div>
      </div>

      <div className="lg:col-span-6">
        <Overline>Pour les particuliers</Overline>
        <h2 className="display text-4xl lg:text-5xl tracking-tighter leading-[0.95] mt-3">
          Vous en avez marre des arnaques et des chantiers abandonnés ?
        </h2>
        <p className="mt-5 text-zinc-700 text-lg">
          Sur Batisur, votre argent ne quitte jamais Stripe avant que <strong>vous-même</strong> ayez vu le travail fini sur photo.
          Aucun acompte versé en aveugle. Aucun chèque envoyé dans le vide.
        </p>

        <div className="mt-8 space-y-4">
          {[
            { fear: "Artisan qui disparaît avec l'acompte ?", solve: "Votre paiement reste bloqué chez Stripe. Pas de travaux livrés, pas de fonds versés." },
            { fear: "Travail mal fait ou inachevé ?", solve: "L'artisan DOIT publier des photos horodatées de l'étape « Finition ». Sans cela, impossible de libérer les fonds." },
            { fear: "Artisan inconnu et louche ?", solve: "Invitez votre propre artisan OU choisissez parmi nos profils « Or » : SIRET vérifié officiel + 10 chantiers terminés minimum." },
          ].map(({ fear, solve }, i) => (
            <div key={i} className="border-2 border-[#0A0A0B] bg-white p-5 shadow-[4px_4px_0_#0A0A0B]" data-testid={`particulier-fear-${i}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FF4D00] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#0A0A0B]">{fear}</div>
                  <div className="text-sm text-zinc-700 mt-1 flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{solve}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Link to="/register?role=demandeur" data-testid="particulier-cta">
          <button className="btn-batisur mt-8 flex items-center gap-2 shadow-[6px_6px_0_#0A0A0B] hover:shadow-[3px_3px_0_#0A0A0B] hover:translate-x-[3px] hover:translate-y-[3px] transition">
            Sécuriser mon prochain chantier <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </div>
  </section>
);

// ── Artisan focus ────────────────────────────────────────────────────────────
const PersonaArtisan = () => (
  <section className="border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white py-20 lg:py-28 relative overflow-hidden">
    <div className="absolute inset-0 bg-grid pointer-events-none opacity-20" />
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center relative">
      <div className="lg:col-span-6">
        <Overline>Pour les artisans</Overline>
        <h2 className="display text-4xl lg:text-5xl tracking-tighter leading-[0.95] mt-3">
          Travaillez l'esprit libre. La fin des retards de paiement.
        </h2>
        <p className="mt-5 text-zinc-300 text-lg">
          Vous voyez l'argent <strong>déjà bloqué chez Stripe</strong> avant de poser le premier coup de marteau.
          Plus de chèques sans provision. Plus de relances à 90 jours.
        </p>

        <div className="mt-8 space-y-4">
          {[
            { fear: "Client mauvais payeur ?", solve: "Les fonds sont déjà séquestrés AVANT le démarrage. Pas de surprise, pas de relance." },
            { fear: "Litige injustifié sur la qualité ?", solve: "Votre journal de chantier (photos + PDF horodatés) fait office de preuve opposable. Médiation Batisur en dernier recours." },
            { fear: "Concurrence déloyale et bouche-à-oreille saturé ?", solve: "Démarquez-vous avec le badge « Or » : SIRET vérifié + 10 chantiers terminés = position prioritaire dans l'annuaire." },
          ].map(({ fear, solve }, i) => (
            <div key={i} className="border-2 border-white/30 bg-white/5 p-5 backdrop-blur-sm" data-testid={`artisan-fear-${i}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FF4D00] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-white">{fear}</div>
                  <div className="text-sm text-zinc-300 mt-1 flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{solve}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Link to="/register?role=artisan" data-testid="artisan-cta">
          <button className="btn-batisur mt-8 flex items-center gap-2 shadow-[6px_6px_0_#FF4D00] hover:shadow-[3px_3px_0_#FF4D00] hover:translate-x-[3px] hover:translate-y-[3px] transition">
            Rejoindre Batisur Pro <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>

      <div className="lg:col-span-6 relative">
        <div className="border-2 border-white/30 bg-white overflow-hidden">
          <img src={WORKER_IMG} alt="Artisan au travail" className="w-full h-[440px] object-cover" />
        </div>
        {/* Floating Gold badge mock */}
        <div className="absolute -top-6 -left-6 bg-white text-[#0A0A0B] border-2 border-[#0A0A0B] p-4 w-72 shadow-[6px_6px_0_#FF4D00]" data-testid="artisan-badge-mock">
          <div className="flex items-center justify-between">
            <div>
              <div className="display text-lg">Plomberie Dupont</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Paris 13ᵉ</div>
            </div>
            <div className="bg-amber-500 text-[#0A0A0B] px-2 py-1 mono text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
              <Award className="w-3 h-3" /> Or
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <BadgeCheck className="w-4 h-4 text-emerald-600" />
            <span className="font-semibold">SIRET vérifié · 23 chantiers</span>
          </div>
          <div className="mt-2 text-xs text-zinc-600">⭐ 4.9/5 (37 avis)</div>
        </div>
      </div>
    </div>
  </section>
);

// ── Workflow Bento ───────────────────────────────────────────────────────────
const Workflow = () => {
  const steps = [
    { n: "01", t: "Devis & invitation", d: "Le demandeur publie sa mission ou invite son artisan préféré. L'artisan envoie son devis numérique.", icon: FileText, col: "lg:col-span-4" },
    { n: "02", t: "Paiement séquestré", d: "Le demandeur paie via Stripe Checkout. Les fonds sont immédiatement bloqués sur compte séquestre.", icon: Lock, col: "lg:col-span-4 bg-[#FF4D00] text-white border-[#FF4D00]" },
    { n: "03", t: "Démarrage des travaux", d: "L'artisan voit le paiement validé et démarre le chantier en toute sécurité.", icon: Hammer, col: "lg:col-span-4" },
    { n: "04", t: "Journal photo obligatoire", d: "L'artisan publie au fil du chantier les photos horodatées de chaque étape : Démarrage, En cours, Finition.", icon: Camera, col: "lg:col-span-7" },
    { n: "05", t: "Validation = libération instantanée", d: "Le demandeur vérifie les preuves, clique sur « Valider et libérer ». Les fonds partent vers l'artisan.", icon: CheckCircle2, col: "lg:col-span-5 bg-emerald-600 text-white border-emerald-600" },
  ];
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <Overline>Workflow Batisur</Overline>
        <h2 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl">
          5 étapes. Aucune zone d'ombre. <span className="text-[#FF4D00]">Aucun risque.</span>
        </h2>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {steps.map((s) => (
            <div
              key={s.n}
              className={`border-2 border-[#0A0A0B] p-6 lg:p-8 shadow-[6px_6px_0_#0A0A0B] hover:translate-y-[-4px] hover:shadow-[8px_10px_0_#0A0A0B] transition ${s.col}`}
              data-testid={`workflow-step-${s.n}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="display text-5xl lg:text-6xl tracking-tighter leading-none opacity-90">{s.n}</div>
                <s.icon className="w-7 h-7" strokeWidth={2} />
              </div>
              <div className="display text-2xl mt-5">{s.t}</div>
              <p className="text-sm mt-3 opacity-90 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Comparison ───────────────────────────────────────────────────────────────
const Comparison = () => {
  const rows = [
    ["Versement de l'acompte", "Direct sur RIB inconnu", "Bloqué chez Stripe"],
    ["Preuves d'avancement", "Parole contre parole", "Journal photo horodaté obligatoire"],
    ["Identité légale de l'artisan", "Vous devez vérifier vous-même", "SIRET contrôlé via Sirene/INSEE"],
    ["Libération des fonds", "À l'aveugle, sur promesse", "Conditionnée à votre validation"],
    ["Certificat de fin de travaux", "Aucun (votre parole)", "PDF signé · opposable à l'assurance"],
    ["Litige sur la qualité", "Tribunal long et coûteux", "Médiation admin avec preuves opposables"],
    ["Facture & TVA", "Difficile à obtenir", "Facture PDF auto + TVA 20% conforme"],
  ];
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-[#F7F7F9] py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <Overline>Le vrai coût du risque</Overline>
        <h2 className="display text-4xl lg:text-6xl tracking-tighter mt-3">
          Sans Batisur vs <span className="text-[#FF4D00]">Avec Batisur</span>
        </h2>
        <p className="mt-5 text-zinc-700 max-w-2xl">
          La commission de 10% paraît élevée jusqu'à ce que vous compariez avec ce que vous risquez réellement
          sur un chantier non-sécurisé.
        </p>

        <div className="mt-12 border-2 border-[#0A0A0B] bg-white shadow-[8px_8px_0_#0A0A0B] overflow-hidden">
          <div className="grid grid-cols-12 border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white">
            <div className="col-span-4 p-4 mono text-[11px] uppercase tracking-widest font-semibold">// Critère</div>
            <div className="col-span-4 p-4 mono text-[11px] uppercase tracking-widest font-semibold flex items-center gap-2 border-l-2 border-white/20">
              <XCircle className="w-4 h-4 text-red-400" /> Sans Batisur
            </div>
            <div className="col-span-4 p-4 mono text-[11px] uppercase tracking-widest font-semibold flex items-center gap-2 border-l-2 border-white/20 bg-[#FF4D00]">
              <CheckCircle2 className="w-4 h-4" /> Avec Batisur
            </div>
          </div>
          {rows.map(([crit, sans, avec], i) => (
            <div key={i} className={`grid grid-cols-12 ${i < rows.length - 1 ? "border-b-2 border-zinc-200" : ""}`} data-testid={`comparison-row-${i}`}>
              <div className="col-span-4 p-4 lg:p-5 font-semibold text-sm">{crit}</div>
              <div className="col-span-4 p-4 lg:p-5 text-sm text-zinc-600 border-l-2 border-zinc-200 flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /> {sans}
              </div>
              <div className="col-span-4 p-4 lg:p-5 text-sm text-emerald-900 bg-emerald-50 border-l-2 border-emerald-200 flex items-start gap-2 font-semibold">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> {avec}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Real testimonials (hidden until real reviews exist) ─────────────────────
const Testimonials = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get("/stats/testimonials").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data || (!data.reviews?.length && !data.top_artisans?.length)) return null;
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <Overline>// Preuve sociale · avis 100% réels</Overline>
        <h2 className="display text-4xl lg:text-5xl mt-3 max-w-3xl">
          Ils ont <span className="text-[#FF4D00]">sécurisé</span> leur chantier.
        </h2>
        {data.reviews?.length > 0 && (
          <div className={`mt-10 grid gap-0 border-2 border-[#0A0A0B] ${data.reviews.length >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : data.reviews.length === 2 ? "md:grid-cols-2" : "max-w-xl"}`}>
            {data.reviews.map((r, i) => (
              <div
                key={i}
                className={`p-6 ${data.reviews.length >= 2 && i % 3 !== 2 && i < data.reviews.length - 1 ? "lg:border-r-2" : ""} ${i < data.reviews.length - 1 ? "border-b-2 lg:border-b-0" : ""} border-[#0A0A0B]`}
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className={`w-4 h-4 ${s < r.rating ? "text-[#FF4D00] fill-[#FF4D00]" : "text-zinc-200"}`} />
                  ))}
                </div>
                <p className="text-sm text-zinc-700 mt-3 leading-relaxed">« {r.comment} »</p>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-4">
                  {r.author} → {r.artisan_name}
                  {r.category && ` · ${r.category}`}
                  {r.city && ` · ${r.city}`}
                </div>
              </div>
            ))}
          </div>
        )}
        {data.top_artisans?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-3">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 w-full">Artisans les mieux notés :</div>
            {data.top_artisans.map((a) => (
              <Link
                key={a.id}
                to={`/artisans/${a.id}`}
                className="border-2 border-[#0A0A0B] px-4 py-2 flex items-center gap-2 hover:bg-[#0A0A0B] hover:text-white transition-colors"
                data-testid={`top-artisan-${a.id}`}
              >
                <span className="font-bold text-sm">{a.name}</span>
                <span className="flex items-center gap-1 text-sm">
                  <Star className="w-3.5 h-3.5 text-[#FF4D00] fill-[#FF4D00]" />
                  {Number(a.rating_avg).toFixed(1)} ({a.rating_count})
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ── 5 Trust Pillars ──────────────────────────────────────────────────────────
const TrustPillars = () => {
  const pillars = [
    { i: ShieldCheck, t: "Escrow Stripe", d: "Stripe Payments est agréé établissement de paiement. Vos fonds sont gérés selon les standards bancaires européens." },
    { i: Camera, t: "Preuves photos opposables", d: "Chaque étape du chantier est documentée par photos horodatées et stockée de façon immuable." },
    { i: BadgeCheck, t: "Vérification SIRET officielle", d: "Connexion directe à la base Sirene/INSEE — état juridique de l'entreprise vérifié en temps réel." },
    { i: Award, t: "Certificat de fin de travaux", d: "PDF signé électroniquement par les 2 parties à la libération. Opposable à l'assurance décennale et utile à la revente du bien." },
    { i: Scale, t: "Médiation administrateur", d: "En cas de litige, un administrateur Batisur peut consulter le dossier complet et trancher avant tribunal." },
  ];
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <Overline>Mécanismes de confiance</Overline>
        <h2 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-3xl">
          5 piliers techniques. Pas du marketing.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {pillars.map(({ i: Icon, t, d }) => (
            <div key={t} className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[4px_4px_0_#0A0A0B] hover:translate-y-[-4px] hover:shadow-[6px_8px_0_#FF4D00] transition" data-testid={`pillar-${t}`}>
              <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center">
                <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="display text-xl mt-5 leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── WhyBatisur — 6 conversion arguments ─────────────────────────────────────
const WhyBatisur = () => {
  const args = [
    {
      i: Lock,
      t: "Fonds séquestrés Stripe",
      d: "L'argent est bloqué chez Stripe (agréé bancaire européen). L'artisan ne peut PAS fuir avec l'acompte. Zéro impayé côté artisan, zéro arnaque côté demandeur.",
      badge: "Anti-arnaque",
      color: "#FF4D00",
    },
    {
      i: Camera,
      t: "Preuves horodatées opposables",
      d: "Photos GPS + timestamp SHA-256 non falsifiables. Le PDF signé sert de preuve devant l'assureur décennale et en justice. Fin du « parole contre parole ».",
      badge: "Recevable en justice",
      color: "#0A0A0B",
    },
    {
      i: Receipt,
      t: "Factur-X 2026 inclus",
      d: "Vos factures sont générées au format PDF/A-3 + XML CII (norme Factur-X obligatoire). Économisez 15€/mois d'abonnement compta — inclus dans le Pack Or.",
      badge: "Conformité 2026",
      color: "#FF4D00",
    },
    {
      i: Handshake,
      t: "Équipe médiation dédiée",
      d: "En cas de litige, notre équipe médiation (personnes réelles, pas un chatbot) analyse votre dossier avec preuves opposables et tranche sous 30 jours max. Assignation automatique par mission.",
      badge: "Humains, pas IA",
      color: "#0A0A0B",
    },
    {
      i: Gift,
      t: "30 jours gratuits Pack Or",
      d: "Testez toutes les fonctionnalités Pack Or (mise en avant + badge officiel + Factur-X) offerts pendant 30 jours dès l'inscription artisan. Aucune CB demandée.",
      badge: "Sans CB",
      color: "#FF4D00",
    },
    {
      i: BadgeCheck,
      t: "SIRET vérifié en temps réel",
      d: "Connexion directe à la base Sirene/INSEE. État juridique, dénomination officielle et NAF contrôlés à la seconde. Adieu les faux artisans et les entreprises radiées.",
      badge: "API Sirene/INSEE",
      color: "#0A0A0B",
    },
  ];
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-white py-20 lg:py-28" data-testid="why-batisur">
      <div className="max-w-7xl mx-auto px-6">
        <Overline>Pourquoi Batisur ?</Overline>
        <h2 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[0.95]">
          6 raisons concrètes de <span className="text-[#FF4D00]">tout sécuriser</span> chez nous.
        </h2>
        <p className="mt-5 text-zinc-700 max-w-2xl text-lg">
          Pas des promesses marketing. Des mécanismes techniques et juridiques que vous ne trouverez nulle part ailleurs
          sur une seule plateforme.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {args.map(({ i: Icon, t, d, badge, color }, idx) => (
            <div
              key={t}
              className="border-2 border-[#0A0A0B] bg-white p-7 shadow-[6px_6px_0_#0A0A0B] hover:translate-y-[-4px] hover:shadow-[8px_10px_0_#FF4D00] transition relative"
              data-testid={`why-arg-${idx}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="w-14 h-14 border-2 border-[#0A0A0B] flex items-center justify-center flex-shrink-0" style={{ background: color }}>
                  <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                </div>
                <span className="mono text-[9px] uppercase tracking-widest font-bold px-2 py-1 border-2 border-[#0A0A0B] bg-[#F7F7F9] text-[#0A0A0B]">
                  {badge}
                </span>
              </div>
              <div className="display text-2xl mt-5 leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 border-2 border-[#FF4D00] bg-orange-50 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Sparkles className="w-8 h-8 text-[#FF4D00] flex-shrink-0" />
            <div>
              <div className="display text-xl leading-tight">Vous cumulez les 6 avantages en 60 secondes</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
                Inscription gratuite · Aucune CB demandée · Résiliable à tout moment
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register?role=demandeur" data-testid="why-cta-demandeur">
              <button className="btn-batisur flex items-center gap-2">
                <UserIcon className="w-4 h-4" /> Je suis demandeur
              </button>
            </Link>
            <Link to="/register?role=artisan&trial=1" data-testid="why-cta-artisan">
              <button className="btn-anthracite flex items-center gap-2">
                <HardHat className="w-4 h-4" /> Je suis artisan
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── MutualInvitation — Featured differentiator ──────────────────────────────
const MutualInvitation = () => (
  <section className="border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white py-20 lg:py-28 relative overflow-hidden" data-testid="mutual-invitation">
    <div className="absolute inset-0 bg-grid pointer-events-none opacity-15" />
    <div className="max-w-7xl mx-auto px-6 relative">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Fonctionnalité phare</div>
          <h2 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[0.95]">
            Gardez vos <span className="text-[#FF4D00]">relations existantes</span>.<br />
            Ajoutez juste la <span className="underline decoration-[#FF4D00] decoration-[6px] underline-offset-4">sécurité</span>.
          </h2>
        </div>
        <p className="text-zinc-300 max-w-md text-lg">
          Vous avez déjà VOTRE artisan de confiance ? Ou VOS clients fidèles ? Batisur fonctionne aussi <strong>avec eux</strong>.
          Vous vous invitez mutuellement en 1 clic, l'autre rejoint en 1 minute.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Demandeur invites Artisan */}
        <div className="border-2 border-white/30 bg-white/5 backdrop-blur-sm p-8 lg:p-10" data-testid="invite-flow-demandeur">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 border-2 border-[#FF4D00] bg-[#FF4D00] flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <ArrowRight className="w-5 h-5 text-[#FF4D00]" />
            <div className="w-12 h-12 border-2 border-white/40 bg-white/10 flex items-center justify-center">
              <HardHat className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">Demandeur → Artisan</div>
          <div className="display text-3xl mt-2 leading-tight">Invitez VOS artisans</div>
          <p className="mt-4 text-zinc-300 text-sm leading-relaxed">
            Vous avez un plombier de confiance, un maçon recommandé par un voisin, une équipe qui vous suit depuis 5 ans ?
            Invitez-les par email depuis votre mission. Ils reçoivent un lien sécurisé, créent leur compte en 1 minute,
            envoient leur devis. Vous choisissez celui qui vous convient.
          </p>

          <div className="mt-6 space-y-3">
            {[
              { icon: Mail, t: "Invitation par email en 1 clic" },
              { icon: Users, t: "Plusieurs artisans invités = plusieurs devis comparables" },
              { icon: BadgeCheck, t: "Chacun est vérifié SIRET dès son inscription" },
              { icon: Lock, t: "Vous restez maître de la sélection — un seul devis accepté" },
            ].map(({ icon: Icon, t }, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <Icon className="w-4 h-4 text-[#FF4D00] flex-shrink-0 mt-0.5" />
                <span className="text-zinc-200">{t}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-white/20">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Exemple d'invitation</div>
            <div className="bg-black/40 border border-white/20 p-4 mono text-xs text-zinc-300 leading-relaxed">
              <div className="text-[#FF4D00]">De : Marie Dubois (Batisur)</div>
              <div>À : jean.plombier@monartisan.fr</div>
              <div className="mt-2 text-white">
                « Salut Jean, on a une salle de bain à refaire à Paris 11. Peux-tu m'envoyer ton devis via Batisur ?
                Cette fois on sécurise via séquestre. Lien d'inscription rapide → »
              </div>
            </div>
          </div>
        </div>

        {/* Artisan invites Demandeur */}
        <div className="border-2 border-[#FF4D00] bg-[#FF4D00]/10 p-8 lg:p-10" data-testid="invite-flow-artisan">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 border-2 border-[#FF4D00] bg-[#FF4D00] flex items-center justify-center">
              <HardHat className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <ArrowRight className="w-5 h-5 text-[#FF4D00]" />
            <div className="w-12 h-12 border-2 border-white/40 bg-white/10 flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">Artisan → Demandeur</div>
          <div className="display text-3xl mt-2 leading-tight">Invitez VOS clients</div>
          <p className="mt-4 text-zinc-300 text-sm leading-relaxed">
            Un client vous rappelle après 3 ans pour de nouveaux travaux ? Un promoteur vous confie un lot ?
            Invitez-le sur Batisur : vous êtes payé 100% garanti par séquestre, il gagne en tranquillité.
            <strong className="text-white"> Vous ne perdez plus jamais de mission sur une question de confiance.</strong>
          </p>

          <div className="mt-6 space-y-3">
            {[
              { icon: Banknote, t: "Vos paiements garantis à 100% par Stripe" },
              { icon: UserPlus, t: "Plusieurs clients invités = pipeline de missions sécurisées" },
              { icon: Receipt, t: "Factures Factur-X 2026 générées automatiquement" },
              { icon: Award, t: "Chaque mission complétée booste votre badge Or" },
            ].map(({ icon: Icon, t }, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <Icon className="w-4 h-4 text-[#FF4D00] flex-shrink-0 mt-0.5" />
                <span className="text-zinc-200">{t}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-white/20">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Cas d'usage typique</div>
            <div className="bg-black/40 border border-white/20 p-4 text-xs text-zinc-300 leading-relaxed">
              <strong className="text-white">« Ma cliente hésitait à me confier ses 40k€ de rénovation sans caution.
              Je l'ai invitée sur Batisur — elle a payé le lendemain. Mission gagnée. »</strong>
              <div className="mt-2 mono text-[10px] uppercase tracking-widest text-zinc-500">— Julien, plombier Lyon</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-2 border-[#FF4D00] bg-[#FF4D00] text-white p-6 lg:p-8">
        <div>
          <div className="display text-2xl lg:text-3xl leading-tight">
            Vous gardez vos gens. Vous ajoutez juste le filet de sécurité.
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/90 mt-2">
            Invitations illimitées · Gratuit tant qu'aucun paiement n'est déclenché
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/register?role=demandeur" data-testid="invite-cta-demandeur">
            <button className="btn-anthracite flex items-center gap-2 shadow-[4px_4px_0_#0A0A0B]">
              Inviter mes artisans <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <Link to="/register?role=artisan&trial=1" data-testid="invite-cta-artisan">
            <button className="btn-anthracite flex items-center gap-2 shadow-[4px_4px_0_#0A0A0B]">
              Inviter mes clients <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// ── Google Review CTA ────────────────────────────────────────────────────────
const GoogleReviewCTA = () => (
  <section className="border-b-2 border-[#0A0A0B] bg-white py-14" data-testid="google-review-section">
    <div className="max-w-5xl mx-auto px-6">
      <div className="border-2 border-[#FF4D00] bg-orange-50 p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 shadow-[6px_6px_0_#0A0A0B]">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-[#FF4D00]">
            {[1,2,3,4,5].map((i) => (
              <svg key={i} width="22" height="22" viewBox="0 0 24 24" fill="#FF4D00" stroke="#FF4D00" strokeWidth="1"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            ))}
          </div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold mt-3">
            // Ils nous ont fait confiance
          </div>
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3 leading-tight">
            Vous avez terminé un chantier avec Batisur ?<br />
            <span className="text-[#FF4D00]">Partagez votre expérience sur Google.</span>
          </h2>
          <p className="mt-4 text-zinc-700 text-sm max-w-2xl">
            Votre avis honnête permet à d'autres particuliers, promoteurs et artisans de découvrir Batisur Connect —
            la seule marketplace française qui bloque vraiment les fonds jusqu'à validation. 30 secondes, ça change tout.
          </p>
        </div>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-review-cta"
          className="flex items-center gap-2 bg-[#0A0A0B] text-white px-6 py-4 border-2 border-[#0A0A0B] hover:bg-[#FF4D00] hover:border-[#FF4D00] transition font-semibold text-sm uppercase tracking-widest whitespace-nowrap shadow-[4px_4px_0_#FF4D00]"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          Laisser un avis Google
        </a>
      </div>
    </div>
  </section>
);

// ── Pricing ──────────────────────────────────────────────────────────────────
const Pricing = () => {
  const tiers = [
    { range: "0 → 5 000 €", rate: "10%" },
    { range: "5 000 → 25 000 €", rate: "7%" },
    { range: "25 000 → 100 000 €", rate: "5%" },
    { range: "100 000 → 500 000 €", rate: "3%" },
    { range: "> 500 000 €", rate: "1,5%" },
  ];
  return (
    <section id="tarifs" className="border-b-2 border-[#0A0A0B] bg-[#F7F7F9] py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6">
        <Overline>Modèle économique</Overline>
        <h2 className="display text-4xl lg:text-6xl tracking-tighter mt-3">
              Commission dégressive. <span className="text-[#FF4D00]">Pas de petits caractères.</span>
        </h2>
        <p className="mt-5 text-zinc-700 max-w-2xl">
          Plus votre chantier est gros, plus la commission baisse. Calcul par palier marginal (comme l'impôt sur le revenu) :
          chaque euro paie le taux de sa tranche, pas le taux maximal.
        </p>

        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 border-2 border-[#0A0A0B] bg-white p-8 lg:p-10 shadow-[6px_6px_0_#0A0A0B]">
            <Overline>Paliers de commission</Overline>
            <div className="mt-5 border-2 border-[#0A0A0B]">
              <div className="grid grid-cols-12 bg-[#0A0A0B] text-white">
                <div className="col-span-8 p-3 mono text-[11px] uppercase tracking-widest font-semibold">// Tranche</div>
                <div className="col-span-4 p-3 mono text-[11px] uppercase tracking-widest font-semibold border-l-2 border-white/20 bg-[#FF4D00]">Par partie</div>
              </div>
              {tiers.map((t, i) => (
                <div key={i} className={`grid grid-cols-12 ${i < tiers.length - 1 ? "border-b-2 border-zinc-200" : ""}`}>
                  <div className="col-span-8 p-3 lg:p-4 font-semibold text-sm">{t.range}</div>
                  <div className="col-span-4 p-3 lg:p-4 display text-2xl border-l-2 border-zinc-200">{t.rate}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs text-zinc-600 border-2 border-zinc-200 bg-[#F7F7F9] p-3">
              <strong>Exemple promoteur — 600 000 € de travaux :</strong><br />
              5k×10% + 20k×7% + 75k×5% + 400k×3% + 100k×1,5% = <strong>19 150 €</strong> par partie
              (taux effectif <strong>3,19%</strong>) au lieu de 60 000 € en taux plat.
            </div>
            <div className="mt-3 text-xs text-zinc-600">
              Facture PDF émise automatiquement avec <strong>TVA 20%</strong> conforme. Aucun frais d'inscription, aucun frais sur devis.
            </div>
          </div>

          <div className="lg:col-span-5 border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-8 lg:p-10 shadow-[6px_6px_0_#FF4D00]">
            <Overline color="#FF4D00">Abonnements artisans</Overline>
            <div className="space-y-4 mt-5">
              <div className="flex items-baseline justify-between border-b border-white/20 pb-3">
                <div>
                  <div className="display text-2xl">Standard</div>
                  <div className="text-xs text-zinc-400">Profil public · devis illimités</div>
                </div>
                <div className="display text-2xl">0€</div>
              </div>
              <div className="flex items-baseline justify-between border-b border-white/20 pb-3">
                <div>
                  <div className="display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FF4D00]" /> Premium</div>
                  <div className="text-xs text-zinc-400">Mise en avant + badge vérifié</div>
                </div>
                <div className="display text-2xl">29€<span className="text-sm text-zinc-400">/30j</span></div>
              </div>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="display text-2xl flex items-center gap-2"><Award className="w-5 h-5 text-amber-400" /> Or</div>
                  <div className="text-xs text-zinc-400">SIRET vérifié + 10 missions · tête d'annuaire</div>
                </div>
                <div className="display text-2xl text-amber-400">49€<span className="text-sm text-zinc-400">/30j</span></div>
              </div>
            </div>
            <Link to="/pro/premium" data-testid="pricing-cta-pro">
              <button className="btn-batisur w-full mt-7 flex items-center justify-center gap-2 shadow-[4px_4px_0_#FF4D00]">
                Découvrir les packs Pro <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── FAQ ──────────────────────────────────────────────────────────────────────
const FAQ = () => {
  const items = [
    {
      q: "Que se passe-t-il si l'artisan abandonne le chantier ?",
      a: "Tant que les fonds sont en séquestre Stripe, l'artisan ne reçoit rien. Vous pouvez demander une médiation à un administrateur Batisur qui consultera le journal de chantier et tranchera : remboursement intégral, partiel, ou libération conditionnelle. En dernier recours, vous récupérez vos fonds.",
    },
    {
      q: "Comment l'artisan est-il sûr d'être payé ?",
      a: "Avant même de commencer les travaux, l'artisan voit dans son dashboard que les fonds sont déjà bloqués chez Stripe. Il sait que l'argent existe et qu'il sera libéré dès qu'il aura publié ses preuves photo de finition et que le demandeur aura validé.",
    },
    {
      q: "Puis-je utiliser Batisur avec MON propre artisan ?",
      a: "Oui — c'est même un cas d'usage central. Vous l'invitez par email depuis votre dashboard, il rejoint Batisur en 1 minute via un lien sécurisé, et vous démarrez le processus escrow ensemble. Vous gardez votre artisan de confiance, vous ajoutez la sécurité. Vous pouvez inviter plusieurs artisans en parallèle pour comparer les devis.",
    },
    {
      q: "Je suis artisan — puis-je inviter mes propres clients sur Batisur ?",
      a: "Absolument. Depuis votre dashboard artisan, invitez vos clients (particuliers, promoteurs, syndics…) par email. Ils reçoivent un lien sécurisé pour créer leur compte demandeur en 1 minute et sécuriser le paiement de vos travaux via séquestre Stripe. C'est LE meilleur argument pour convaincre un client hésitant à vous confier un gros chantier : il paie en séquestre, vous êtes payé garanti à 100%. Invitations illimitées et gratuites.",
    },
    {
      q: "Comment fonctionne le journal photo et la validation ?",
      a: "L'artisan publie au fil du chantier des photos (et PDF si besoin) classées par étape : Démarrage, En cours, Étape clé, Finition. Le bouton « Valider et libérer » ne s'active QUE si l'artisan sélectionné a publié au moins une photo d'étape « Finition ». Vous décidez ensuite si vous validez.",
    },
    {
      q: "Pourquoi 10% de commission de chaque côté ?",
      a: "Cela couvre les frais Stripe (séquestre + virements), l'infrastructure technique sécurisée, l'hébergement des preuves photo, la vérification SIRET en temps réel, la médiation admin en cas de litige, et l'émission automatique du certificat de fin de travaux. Comparé à un chantier mal mené, c'est dérisoire.",
    },
    {
      q: "À quoi sert exactement le certificat de fin de travaux ?",
      a: "Une fois les fonds libérés, Batisur génère automatiquement un PDF horodaté contenant les photos de finition, les montants, et deux signatures électroniques SHA-256 (vous + l'artisan). C'est un document opposable que vous pouvez présenter à votre assureur dans le cadre de la garantie décennale, ou produire lors d'une revente immobilière pour prouver la réception des travaux.",
    },
    {
      q: "Les factures sont-elles conformes à la fiscalité française ?",
      a: "Oui. Batisur émet automatiquement des factures PDF avec TVA 20%, numérotation séquentielle, mentions légales, IBAN d'encaissement, identité Batisur SAS — entièrement opposables à l'administration fiscale et téléchargeables par les deux parties.",
    },
    {
      q: "Mes données et photos sont-elles protégées ?",
      a: "Les photos ne sont accessibles qu'aux deux parties (demandeur + artisan sélectionné) + admin Batisur en cas de litige. Aucune publication publique sans accord. Stripe gère le paiement selon les standards PCI-DSS. Données hébergées en Europe.",
    },
    {
      q: "Combien de temps prend la libération des fonds après validation ?",
      a: "Instantanée côté plateforme. Côté banque de l'artisan, le virement Stripe apparaît sous 24-48h ouvrées selon la banque. Pas d'attente arbitraire imposée par Batisur.",
    },
  ];
  return (
    <section className="border-b-2 border-[#0A0A0B] bg-white py-20 lg:py-28">
      <div className="max-w-3xl mx-auto px-6">
        <Overline>Questions fréquentes</Overline>
        <h2 className="display text-4xl lg:text-5xl tracking-tighter mt-3">
          Toutes vos questions. <span className="text-[#FF4D00]">Aucune zone floue.</span>
        </h2>

        <div className="mt-10 border-2 border-[#0A0A0B] bg-white shadow-[6px_6px_0_#0A0A0B]">
          <Accordion type="single" collapsible className="w-full">
            {items.map((it, i) => (
              <AccordionItem key={i} value={`item-${i}`} className={`${i < items.length - 1 ? "border-b-2 border-zinc-200" : ""} border-0`}>
                <AccordionTrigger
                  className="px-6 py-5 hover:no-underline text-left font-semibold text-[#0A0A0B] hover:bg-[#F7F7F9] data-[state=open]:bg-[#0A0A0B] data-[state=open]:text-white transition"
                  data-testid={`faq-trigger-${i}`}
                >
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 pt-3 text-zinc-700 leading-relaxed">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// ── Split CTA ────────────────────────────────────────────────────────────────
const SplitCTA = () => (
  <section className="border-b-2 border-[#0A0A0B] grid lg:grid-cols-2">
    <div className="bg-[#F7F7F9] p-12 lg:p-20 border-b-2 lg:border-b-0 lg:border-r-2 border-[#0A0A0B] relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="relative">
        <Overline>Vous êtes particulier</Overline>
        <h3 className="display text-4xl lg:text-5xl tracking-tighter mt-3 leading-tight">
          Sécurisez vos travaux<br /><span className="text-[#FF4D00]">dès aujourd'hui</span>.
        </h3>
        <p className="mt-5 text-zinc-700 max-w-md">
          Inscription gratuite. Aucun engagement. Aucun versement tant que vous n'avez pas trouvé votre artisan.
        </p>
        <Link to="/register?role=demandeur" data-testid="footer-cta-client">
          <button className="btn-batisur mt-8 flex items-center gap-2 shadow-[6px_6px_0_#0A0A0B] hover:shadow-[3px_3px_0_#0A0A0B] hover:translate-x-[3px] hover:translate-y-[3px] transition">
            <UserIcon className="w-4 h-4" /> Démarrer mon projet — protégé <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </div>
    <div className="bg-[#0A0A0B] text-white p-12 lg:p-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-15 pointer-events-none" />
      <div className="relative">
        <Overline color="#FF4D00">Vous êtes artisan</Overline>
        <h3 className="display text-4xl lg:text-5xl tracking-tighter mt-3 leading-tight">
          Garantissez tous<br /><span className="text-[#FF4D00]">vos paiements</span>.
        </h3>
        <p className="mt-5 text-zinc-300 max-w-md">
          Rejoignez Batisur Pro. SIRET vérifié, badge officiel, mise en avant dans l'annuaire — fin des chèques sans provision.
        </p>
        <Link to="/register?role=artisan" data-testid="footer-cta-artisan">
          <button className="btn-batisur mt-8 flex items-center gap-2 shadow-[6px_6px_0_#FF4D00] hover:shadow-[3px_3px_0_#FF4D00] hover:translate-x-[3px] hover:translate-y-[3px] transition">
            <HardHat className="w-4 h-4" /> Tester les outils artisan <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </div>
  </section>
);

// ── Page ─────────────────────────────────────────────────────────────────────
import SEO from "@/components/SEO";
import { getSEO, GOOGLE_REVIEW_URL } from "@/lib/seo-config";

export default function Landing() {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      // Wait for sections to be rendered before scrolling
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <SEO cfg={getSEO("/")} />
      <Navbar />
      <Hero />
      <Marquee />
      <WhyBatisur />
      <MutualInvitation />
      <AudienceTargets />
      <SocialProof />
      <TrustStats />
      <Testimonials />
      <PersonaParticulier />
      <PersonaArtisan />
      <Workflow />
      <Comparison />
      <TrustPillars />
      <GoogleReviewCTA />
      <WaitingProspectsWidget variant="landing" limit={6} />
      <Pricing />
      <FAQ />
      <SplitCTA />
      <Footer />
    </div>
  );
}
